import { Formik, Form, FieldArray } from "formik";
import * as Yup from "yup";
import ButtonWithLoader from "@/common/components/ui/loading/ButtonLoading";
import InputFormik from "@/common/components/form/InputFormik";
import { IContractB2B } from "@/types/contractB2B.type";
import { IVehicle } from "@/types/vehicle.type";
import { useEffect, useState } from "react";
import { useToastContext } from "@/common/components/ui/toast/toast.context";
import { useParams } from "react-router-dom";
import { getAvailableVehicles } from "@/common/services/contracts-b2b.api";

// Esquema de validação com Yup
const validationSchema = Yup.object().shape({
  valor: Yup.string().required("Valor é obrigatório."),
  vencimento: Yup.date().required("Vencimento é obrigatório."),
  vehicles: Yup.array()
    .min(1, "O contrato deve ter pelo menos 1 veículo.")
    .of(
      Yup.object().shape({
        licensePlate: Yup.string().required(),
      })
    ),
});

type FormValues = Pick<IContractB2B, "valor" | "vencimento" | "vehicles">;

type FormPropsCreateContractB2B = {
  onSubmit: (values: FormValues & { idCompanyB2B: string }) => Promise<void>;
  initialData?: IContractB2B;
};

function FormCreateContractB2B(props: FormPropsCreateContractB2B) {
  const { id: idCompanyB2B } = useParams<{ id: string }>();
  const { notifyError } = useToastContext();
  const [availableVehicles, setAvailableVehicles] = useState<IVehicle[]>([]);
  const [loadingVehicles, setLoadingVehicles] = useState(true);
  const [hasLoadedVehicles, setHasLoadedVehicles] = useState(false);

  const initialValues: FormValues = props.initialData
    ? {
      valor: props.initialData.valor,
      vencimento: new Date(props.initialData.vencimento)
        .toISOString()
        .split("T")[0] as unknown as Date,
      vehicles: props.initialData.vehicles || [],
    }
    : {
      valor: "",
      vencimento: "" as unknown as Date,
      vehicles: [],
    };

  useEffect(() => {
    if (idCompanyB2B && !hasLoadedVehicles) {
      setLoadingVehicles(true);
      getAvailableVehicles(idCompanyB2B as string)
        .then((result) => {
          if (result.ok && result.data) {
            setAvailableVehicles(result.data);
          } else {
            notifyError("Erro ao carregar veículos disponíveis.");
          }
        })
        .catch(() => notifyError("Erro de comunicação ao buscar veículos."))
        .finally(() => {
          setLoadingVehicles(false);
          setHasLoadedVehicles(true);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idCompanyB2B, hasLoadedVehicles]);

  const handleSubmit = async (
    values: FormValues,
    { setSubmitting }
  ) => {
    if (!idCompanyB2B) return notifyError("ID da Empresa B2B não encontrado.");

    setSubmitting(true);
    await props.onSubmit({ ...values, idCompanyB2B });
    setSubmitting(false);
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      enableReinitialize={true}
    >
      {({ isSubmitting, handleSubmit, values }) => (
        <Form className="flex flex-col gap-4">
          <InputFormik
            label="Valor do Contrato:"
            name="valor"
            className="w-full"
            required={true}
            type="text"
          />
          <InputFormik
            label="Vencimento:"
            name="vencimento"
            className="w-full"
            required={true}
            type="date"
          />

          <FieldArray name="vehicles">
            {({ remove, push }) => (
              <div className="border p-4 rounded-lg space-y-3">
                <h4 className="text-md font-semibold">
                  Veículos no Contrato ({values.vehicles.length})
                </h4>

                <select
                  className="w-full border p-2 rounded"
                  disabled={loadingVehicles}
                  onChange={(e) => {
                    const licensePlate = e.target.value;
                    if (
                      licensePlate &&
                      !values.vehicles.some(
                        (v) => v.licensePlate === licensePlate
                      )
                    ) {
                      push({ licensePlate });
                    }
                    e.target.value = "";
                  }}
                  value=""
                >
                  <option value="" disabled>
                    {loadingVehicles
                      ? "Carregando Veículos..."
                      : "Selecione um Veículo Disponível"}
                  </option>
                  {availableVehicles
                    .filter(
                      (v) =>
                        !values.vehicles.some(
                          (cv) => cv.licensePlate === v.licensePlate
                        )
                    )
                    .map((vehicle) => (
                      <option key={vehicle._id} value={vehicle.licensePlate}>
                        {vehicle.licensePlate} - {vehicle.brand} {vehicle.model}
                      </option>
                    ))}
                </select>

                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {values.vehicles.map((vehicle, index) => (
                    <div
                      key={index}
                      className="flex justify-between items-center bg-gray-100 p-2 rounded"
                    >
                      <span>Placa: **{vehicle.licensePlate}**</span>
                      <button
                        type="button"
                        onClick={() => remove(index)}
                        className="text-red-500 hover:text-red-700 font-semibold text-sm"
                      >
                        Remover
                      </button>
                    </div>
                  ))}
                </div>
                {values.vehicles.length === 0 && (
                  <p className="text-sm text-red-500">
                    Adicione pelo menos um veículo.
                  </p>
                )}
              </div>
            )}
          </FieldArray>

          <div className="flex justify-end pt-4">
            <ButtonWithLoader isLoading={isSubmitting} onClick={handleSubmit}>
              Cadastrar Contrato
            </ButtonWithLoader>
          </div>
        </Form>
      )}
    </Formik>
  );
}

export default FormCreateContractB2B;
